import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Eye, EyeOff, ArrowLeft, Sparkles, Mail, Lock } from "lucide-react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import { getPendingTest, clearPendingTest } from "../../utils/pendingTestStorage";

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [loginSuccess, setLoginSuccess] = useState(false);

  // user added so we can check userType
  const { login, googleSignIn, user } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);

  // Handle redirect after login when user object becomes available
  useEffect(() => {
    const handlePostLoginRedirect = async () => {
      if (!loginSuccess || !user) return;

      // Only handle jobseeker redirect logic
      if (user.userType !== "jobseeker") {
        navigate("/");
        return;
      }

      // Check for pending test
      const pendingTest = getPendingTest();

      if (pendingTest && pendingTest.bypassVerification) {
        // HR-shared test - bypass verification and go directly to test
        clearPendingTest();
        navigate(`/job-seeker/take-test/${pendingTest.testId}?source=hr-shared`);
      } else if (pendingTest) {
        // Legacy pending test without bypass flag - check verification status
        try {
          const profileDoc = await getDoc(doc(db, "userProfiles", user.id));
          const profileData = profileDoc.exists() ? profileDoc.data() : null;

          const hasResume = profileData?.resumeUploaded === true;
          const isVerified = profileData?.skillsVerified === true;

          if (isVerified) {
            // User is verified - go directly to the pending test
            clearPendingTest();
            navigate(`/job-seeker/take-test/${pendingTest.testId}`);
          } else if (hasResume) {
            // Has resume but not verified - go to skills validation
            navigate("/job-seeker/skills-validation");
          } else {
            // No resume - go to resume upload
            navigate("/job-seeker/resume-upload");
          }
        } catch (error) {
          console.error("Error checking user profile:", error);
          navigate("/job-seeker/resume-upload");
        }
      } else {
        // No pending test - normal flow to dashboard
        navigate("/job-seeker/dashboard");
      }
    };

    handlePostLoginRedirect();
  }, [loginSuccess, user, navigate, db]);

  const redirectAfterLogin = () => {
    // Set login success flag to trigger the useEffect
    setLoginSuccess(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const success = await login(formData.email, formData.password);
      if (success) {
        redirectAfterLogin();
      } else {
        setError("Invalid email or password. Please try again.");
      }
    } catch (err) {
      setError("Invalid credentials. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError("");
    try {
      const success = await googleSignIn();
      if (success) {
        redirectAfterLogin();
      } else {
        setError("Google sign-in failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden relative">
      {/* Animated Background Elements */}
      <style>{`
        @keyframes float-1 {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-40px) translateX(-10px); }
          75% { transform: translateY(-20px) translateX(10px); }
        }
        @keyframes float-2 {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          25% { transform: translateY(20px) translateX(-10px); }
          50% { transform: translateY(40px) translateX(10px); }
          75% { transform: translateY(20px) translateX(-10px); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        .animate-float-1 { animation: float-1 8s ease-in-out infinite; }
        .animate-float-2 { animation: float-2 10s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 4s ease-in-out infinite; }
      `}</style>

      {/* Floating animated background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-primary/5 rounded-full animate-float-1 blur-3xl"></div>
        <div className="absolute bottom-32 left-20 w-96 h-96 bg-red-200/5 rounded-full animate-float-2 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-blue-200/5 rounded-full animate-pulse-glow blur-3xl"></div>
      </div>

      {/* Modern Navigation */}
      <nav className="relative bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 z-10">
        <div className="max-w-7xl mx-auto flex justify-start items-center">
          <Link to="/" className="flex items-center gap-0 -ml-1 group">
            <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8 sm:h-9 sm:w-9" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent group-hover:from-red-800 group-hover:via-primary group-hover:to-red-800 transition-all duration-300">
              VetriaAI
            </span>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative min-h-[calc(100vh-73px)] flex items-center justify-center py-8 sm:py-10 px-4 sm:px-6 overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-50/50 via-white to-blue-50/50 opacity-60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.08),transparent_50%)]"></div>

        <div className="relative max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-7 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/80 backdrop-blur-md rounded-full shadow-lg border border-gray-200 mb-5 hover:shadow-xl transition-all duration-300">
              <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-sm font-semibold text-gray-700">Welcome Back</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Sign in to your account
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Access your dashboard and continue your journey
            </p>
          </div>

          {/* Login Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-100 p-7 space-y-5 hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1">
            {/* Google Sign In */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 border-2 border-gray-200 py-3 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all duration-300 font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                className="w-5 h-5"
              />
              <span>Continue with Google</span>
            </button>

            {/* Divider */}
            <div className="relative text-center">
              <span className="bg-white/95 px-4 text-sm text-gray-500">or</span>
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50/80 backdrop-blur-sm border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r-lg shadow-md animate-pulse">
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-button focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-sm"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-11 py-3 border border-gray-300 rounded-button focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-sm"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-700"
                  >
                    Remember me
                  </label>
                </div>
                <Link
                  to="#"
                  className="text-sm text-primary hover:text-primary-hover font-medium transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-white py-3 px-6 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>

          </div>

          {/* SaaS Model - Contact for Access */}
          <div className="mt-5 text-center">
            <p className="text-sm text-gray-600 mb-3">
              Need access to VetriaAI?
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center text-sm font-medium text-primary hover:text-primary-hover transition-colors"
            >
              Contact us for a demo
              <ArrowLeft className="w-4 h-4 ml-1 rotate-180" />
            </Link>
          </div>

          {/* Additional Info */}
          <p className="mt-5 text-center text-sm text-gray-500">
            By signing in, you agree to our{" "}
            <Link to="/terms" className="text-primary hover:text-primary-hover font-medium transition-colors">
              Terms & Conditions
            </Link>
            {" "}and{" "}
            <Link to="/privacy" className="text-primary hover:text-primary-hover font-medium transition-colors">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
